import React from 'react';

import { useClaim } from '~/components/ClaimContainer';
import { useCms } from '~/components/hooks/useCms';
import useDataFetcher from '~/components/useDataFetcher';
import { isFeatureEnabled } from '~/Utils';
import { getSubOrganizationId } from '~/Utils/ClaimUtils';

import type { CliveConfigBooleanKey } from './CliveConfigurationContext';
import { useCliveConfigOptions } from './CliveConfigurationContext';

interface CliveClaimContextValue {
  isClaimChatEnabled: boolean;
  isLoading: boolean;
  isCliveEnabled: (subOrganizationId?: number | null) => boolean;
  isCliveFeatureEnabled: (featureFlag: string) => boolean;
  isCliveConfigEnabled: (configKey: CliveConfigBooleanKey) => boolean;
}

interface ChatPermissionResponse {
  is_allowed: boolean;
}

const CliveClaimContext = React.createContext<CliveClaimContextValue>({
  isClaimChatEnabled: false,
  isLoading: true,
  isCliveEnabled: () => false,
  isCliveFeatureEnabled: () => false,
  isCliveConfigEnabled: () => false,
});

interface CliveClaimContextProviderProps {
  children: React.ReactNode;
}

export const CliveClaimContextProvider: React.FC<CliveClaimContextProviderProps> = ({ children }) => {
  const { claim } = useClaim({ claimRequired: true });
  const { userOrganization } = useCms();
  const {
    isLoading: isCliveConfigLoading,
    isCliveEnabled,
    isCliveConfigEnabled: isCliveConfigEnabledForSubOrg,
  } = useCliveConfigOptions();
  const subOrganizationId = getSubOrganizationId(claim);

  const { data: claimChatPermission, isLoading: isClaimPermissionLoading } = useDataFetcher<ChatPermissionResponse>(
    `/api/v1/claims/${claim.id}/chat/is_allowed`,
    {},
    {
      shouldFetch: !!claim?.id,
    }
  );

  const isClaimChatEnabled = React.useMemo(() => {
    const isClaimAllowed = claimChatPermission?.is_allowed === true;
    return isCliveEnabled(subOrganizationId) && isClaimAllowed;
  }, [isCliveEnabled, claimChatPermission, subOrganizationId]);

  const isCliveFeatureEnabled = React.useCallback(
    (featureFlag: string) => {
      const isCliveEnabledForSubOrg = isCliveEnabled(subOrganizationId);
      const hasFeatureFlag = isFeatureEnabled(userOrganization, featureFlag);
      return isCliveEnabledForSubOrg && hasFeatureFlag;
    },
    [isCliveEnabled, userOrganization, subOrganizationId]
  );

  // No `isCliveEnabled(subOrganizationId) &&` conjunct here, unlike isCliveFeatureEnabled: the server's
  // get_effective_ai_configuration already forces every config key to false when send_ai_data is off,
  // so the value arriving here is already the effective one.
  const isCliveConfigEnabled = React.useCallback(
    (configKey: CliveConfigBooleanKey) => isCliveConfigEnabledForSubOrg(configKey, subOrganizationId),
    [isCliveConfigEnabledForSubOrg, subOrganizationId]
  );

  const contextValue = React.useMemo(
    () => ({
      isClaimChatEnabled,
      isLoading: isClaimPermissionLoading || isCliveConfigLoading,
      isCliveEnabled,
      isCliveFeatureEnabled,
      isCliveConfigEnabled,
    }),
    [
      isClaimChatEnabled,
      isClaimPermissionLoading,
      isCliveConfigLoading,
      isCliveEnabled,
      isCliveFeatureEnabled,
      isCliveConfigEnabled,
    ]
  );

  return <CliveClaimContext.Provider value={contextValue}>{children}</CliveClaimContext.Provider>;
};

export const useCliveClaimContext = (): CliveClaimContextValue => {
  const context = React.useContext(CliveClaimContext);

  if (!context) {
    throw new Error('useCliveClaimContext must be used within a CliveClaimContextProvider');
  }

  return context;
};
